/**
 * Language Switcher Component
 * Allows users to switch between English and Portuguese (BR)
 */

import { useTranslation } from 'react-i18next';
import { useUIStore } from '../../store/uiStore';
import { useUpdatePreferences } from '../../hooks/useUserPreferences';
import { backendLanguageMap, type SupportedLanguage } from '../../i18n';
import type { Language } from '../../types';

interface LanguageSwitcherProps {
  /** Callback when language changes */
  onLanguageChange?: (language: Language) => void;
}

export function LanguageSwitcher({ onLanguageChange }: LanguageSwitcherProps) {
  const { t, i18n } = useTranslation();
  const { theme } = useUIStore();
  const updatePreferences = useUpdatePreferences();
  const isLight = theme === 'light';

  const currentLanguage = i18n.language as SupportedLanguage;

  const handleLanguageChange = async (newLang: SupportedLanguage) => {
    // Update UI immediately
    await i18n.changeLanguage(newLang);
    localStorage.setItem('ff_language', newLang);

    // Persist to backend
    const backendLang = backendLanguageMap[newLang] as Language;
    updatePreferences.mutate(
      { language: backendLang },
      {
        onSuccess: () => {
          onLanguageChange?.(backendLang);
        },
      }
    );
  };

  return (
    <div className="flex items-center justify-between p-4">
      <div className="flex items-center gap-3">
        <svg
          className={`w-5 h-5 ${isLight ? 'text-slate-500' : 'text-slate-400'}`}
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M3 5h12M9 3v2m1.048 9.5A18.022 18.022 0 016.412 9m6.088 9h7M11 21l5-10 5 10M12.751 5C11.783 10.77 8.07 15.61 3 18.129"
          />
        </svg>
        <span className={isLight ? 'text-slate-700' : 'text-slate-200'}>
          {t('pages.settings.language')}
        </span>
      </div>

      <select
        value={currentLanguage}
        onChange={(e) => handleLanguageChange(e.target.value as SupportedLanguage)}
        disabled={updatePreferences.isPending}
        data-testid="language-selector"
        className={`
          px-3 py-1.5 rounded-lg text-sm font-medium
          border outline-none transition-colors cursor-pointer
          ${isLight
            ? 'bg-white border-slate-300 text-slate-700 hover:border-slate-400'
            : 'bg-slate-800 border-slate-600 text-slate-200 hover:border-slate-500'
          }
          ${updatePreferences.isPending ? 'opacity-50 cursor-not-allowed' : ''}
        `}
      >
        <option value="en">{t('pages.settings.english')}</option>
        <option value="pt_br">{t('pages.settings.portuguese')}</option>
      </select>
    </div>
  );
}
